import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FormError } from '@/components/form';
import { AuthShell } from '@/features/auth/auth-shell';
import { api, errorMessage } from '@/lib/api';
import { meQuery } from '@/lib/auth';

export const Route = createFileRoute('/login')({
  validateSearch: (s: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof s.redirect === 'string' && s.redirect.startsWith('/') ? s.redirect : undefined,
  }),
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (me?.mfaVerified) throw redirect({ to: '/' });
  },
  component: LoginPage,
});

function LoginPage() {
  const { redirect: next } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const login = useMutation({
    mutationFn: () => api.post<{ status: 'MFA_REQUIRED' | 'MFA_ENROLMENT_REQUIRED' }>('/v1/auth/login', { email, password }),
    onSuccess: async ({ status }) => {
      queryClient.removeQueries({ queryKey: ['me'] });
      await navigate({ to: status === 'MFA_REQUIRED' ? '/verify' : '/setup-2fa', search: { redirect: next } });
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    login.mutate();
  }

  return (
    <AuthShell
      title="Sign in"
      description="Use your Fraud Safe account. You’ll need your authenticator app next."
      footer={<span>Access is by invitation from your administrator.</span>}
    >
      <form onSubmit={submit} className="grid gap-4" noValidate>
        <FormError message={login.isError ? errorMessage(login.error) : null} />
        <Field id="email" label="Email">
          <Input id="email" type="email" autoComplete="username" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field id="password" label="Password">
          <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Button type="submit" size="lg" disabled={login.isPending || !email || !password}>
          {login.isPending ? 'Signing in…' : 'Continue'}
        </Button>
        <Link to="/forgot-password" className="justify-self-center text-sm text-link underline-offset-4 hover:underline">
          Forgot your password?
        </Link>
      </form>
    </AuthShell>
  );
}
